import { Component } from 'react';

import { getDatadog } from '../../services/datadog';

import type { ReactNode, ErrorInfo } from 'react';
import type { Logger } from '@datadog/browser-logs';

interface Props {
  children?: ReactNode;
}

type State = {
  hasError: boolean;
  componentName?: string;
};

export class ComponentErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  logger: Promise<Logger>;

  constructor(props: Props) {
    super(props);

    this.logger = getDatadog().then((dd) =>
      dd.createLogger('component-error-boundary')
    );
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentName = /at ([^ ]+)/g.exec(errorInfo.componentStack)?.[1];

    this.setState({ componentName });

    this.logger
      .then((logger) => {
        logger.error(
          `component did catch: ${error.message}`,
          { react: { 'page.component': componentName } },
          error
        );
      })
      .catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      if (process.env.NODE_ENV !== 'production') {
        /* eslint-disable i18next/no-literal-string */
        return (
          <p
            style={{
              padding: '1em',
              fontWeight: 'bold',
              background: '#B00',
            }}
          >
            Unable to render {this.state.componentName} component
          </p>
        );
        /* eslint-enable i18next/no-literal-string */
      }
      return null;
    }

    return this.props.children;
  }
}
